import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { useGetChatSession, getGetChatSessionQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CheckCircle2, ChevronRight, AlertCircle, Clock } from "lucide-react";
import { ParticipantInfoGate } from "@/components/participant-info-gate";
import { ParticipantRatingWidget } from "@/components/participant-rating-widget";

const MC_BLOCKS: {
  blockNumber: number;
  theme: string;
  statements: { dim: "D" | "I" | "S" | "C"; text: string }[];
}[] = [
  {
    blockNumber: 1,
    theme: "Liderança",
    statements: [
      { dim: "D", text: "Assumo o controle da situação imediatamente e tomo decisões sem esperar pelo consenso do grupo." },
      { dim: "I", text: "Inspiro e energizo a equipe em torno de uma visão compartilhada, liderando pelo entusiasmo e pelo exemplo." },
      { dim: "S", text: "Cuido para que todos estejam alinhados e confortáveis antes de avançar, priorizando o bem-estar do time." },
      { dim: "C", text: "Planejo cada detalhe meticulosamente, defino papéis e processos com precisão antes de iniciar qualquer ação." },
    ],
  },
  {
    blockNumber: 2,
    theme: "Comunicação",
    statements: [
      { dim: "D", text: "Vou direto ao ponto, sou breve e objetivo — entrego a mensagem principal sem rodeios ou contexto desnecessário." },
      { dim: "I", text: "Uso histórias envolventes e linguagem expressiva para criar conexão emocional com quem me ouve." },
      { dim: "S", text: "Adapto minha mensagem ao contexto de cada pessoa e garanto que todos se sintam incluídos e compreendidos." },
      { dim: "C", text: "Estruturo minha comunicação com lógica, dados e evidências, sendo completo e preciso acima de tudo." },
    ],
  },
  {
    blockNumber: 3,
    theme: "Tomada de decisão",
    statements: [
      { dim: "D", text: "Decido rapidamente com as informações que tenho, assumindo o risco conscientemente e movendo adiante." },
      { dim: "I", text: "Confio na minha intuição e no otimismo de que vai dar certo, preferindo experimentar a ficar paralisado." },
      { dim: "S", text: "Busco consenso e me alinho com pessoas de confiança antes de decidir, evitando gerar instabilidade." },
      { dim: "C", text: "Coleto mais informações antes de decidir, analisando dados e avaliando riscos com cuidado." },
    ],
  },
  {
    blockNumber: 4,
    theme: "Trabalho em equipe",
    statements: [
      { dim: "D", text: "Defino metas claras e cobro desempenho do grupo sem hesitar, priorizando eficiência e resultado." },
      { dim: "I", text: "Uno as pessoas em torno de uma visão comum, usando energia e alegria para resolver tensões no time." },
      { dim: "S", text: "Coopero ativamente, adapto meu estilo ao grupo e apoio quem está com dificuldade, evitando conflito." },
      { dim: "C", text: "Defino papéis, responsabilidades e padrões de qualidade com clareza, cobrando rigor técnico e método." },
    ],
  },
  {
    blockNumber: 5,
    theme: "Conflito",
    statements: [
      { dim: "D", text: "Confronto diretamente sem rodeios e defendo minha posição com firmeza, sem medo de gerar tensão." },
      { dim: "I", text: "Busco reconciliação, uso carisma para suavizar a tensão e crio um ambiente onde todos se sintam ouvidos." },
      { dim: "S", text: "Cedo ou recuo para preservar a paz, preferindo harmonia a ter razão na situação." },
      { dim: "C", text: "Analiso os fatos objetivamente, apresento argumentos lógicos com dados e separo emoção da discussão." },
    ],
  },
  {
    blockNumber: 6,
    theme: "Mudança",
    statements: [
      { dim: "D", text: "Enxergo a mudança como oportunidade de influência e me posiciono rapidamente na nova estrutura." },
      { dim: "I", text: "Fico empolgado com a novidade e inspiro os colegas com uma visão positiva da transformação." },
      { dim: "S", text: "Sinto desconforto com a instabilidade e busco garantias antes de me mover, preferindo mudanças graduais." },
      { dim: "C", text: "Preciso entender detalhadamente o racional, o processo e as implicações antes de aceitar qualquer mudança." },
    ],
  },
  {
    blockNumber: 7,
    theme: "Prazos",
    statements: [
      { dim: "D", text: "Foco no essencial e corto o restante sem hesitar, entregando no prazo mesmo que signifique abrir mão da perfeição." },
      { dim: "I", text: "Uso a urgência como motivação, improviso com criatividade e mantenho o ânimo alto mesmo sob pressão." },
      { dim: "S", text: "Mantenho a calma e trabalho metodicamente mesmo sob pressão, cumprindo o que foi comprometido." },
      { dim: "C", text: "Organizo uma lista detalhada de prioridades e busco maximizar a qualidade dentro do tempo disponível." },
    ],
  },
  {
    blockNumber: 8,
    theme: "Detalhamento",
    statements: [
      { dim: "D", text: "Produzo sínteses objetivas focadas em resultados, acreditando que um bom documento cabe em poucas páginas." },
      { dim: "I", text: "Crio documentos visualmente atrativos e engajantes, usando linguagem dinâmica para inspirar o leitor." },
      { dim: "S", text: "Elaboro com cuidado e paciência, garantindo que todas as partes relevantes estejam cobertas com confiabilidade." },
      { dim: "C", text: "Mergulho em profundidade no tema, verifico cada dado e garanto precisão absoluta, sem abrir mão da qualidade técnica." },
    ],
  },
  {
    blockNumber: 9,
    theme: "Relacionamento",
    statements: [
      { dim: "D", text: "Estabeleço relações com base em competência e respeito mútuo, indo direto ao assunto desde o início." },
      { dim: "I", text: "Invisto fortemente em criar conexão pessoal desde o início, usando carisma e interesse genuíno pelo outro." },
      { dim: "S", text: "Construo relações com sinceridade e paciência, demonstrando lealdade e interesse genuíno de forma gradual." },
      { dim: "C", text: "Construo relações com base em credibilidade e consistência técnica, demonstrando seriedade e respeito." },
    ],
  },
  {
    blockNumber: 10,
    theme: "Motivação",
    statements: [
      { dim: "D", text: "Me motiva a conquista e o desafio em si — a resistência aumenta minha determinação de superar." },
      { dim: "I", text: "Me motiva o reconhecimento, a visibilidade e o impacto que causo nas pessoas ao meu redor." },
      { dim: "S", text: "Me motiva a segurança e o bem-estar das pessoas próximas — família, equipe e relações de confiança." },
      { dim: "C", text: "Me motiva a busca de excelência e aprendizado, usando momentos difíceis como oportunidade de melhoria." },
    ],
  },
  {
    blockNumber: 11,
    theme: "Estresse",
    statements: [
      { dim: "D", text: "Fico mais intenso e controlador, aumentando minha força de vontade para dominar a situação." },
      { dim: "I", text: "Fico mais emocional e impulsivo, buscando suporte e validação das pessoas ao redor para não me sentir sozinho." },
      { dim: "S", text: "Calo, internalizo e me retraio, evitando conflitos e absorvendo tudo em silêncio para não incomodar." },
      { dim: "C", text: "Isolo-me em análise, fico hipercrítico e verifico tudo várias vezes antes de agir, com medo de errar." },
    ],
  },
  {
    blockNumber: 12,
    theme: "Planejamento",
    statements: [
      { dim: "D", text: "Defino metas ambiciosas e um plano focado em resultados, com pouca tolerância para rotinas sem propósito." },
      { dim: "I", text: "Prefiro flexibilidade e espontaneidade, trabalhando melhor quando tenho liberdade para seguir a inspiração do momento." },
      { dim: "S", text: "Crio uma rotina consistente e previsível, sentindo conforto na repetição de boas práticas e no progresso gradual." },
      { dim: "C", text: "Elaboro um cronograma detalhado com metodologia clara, processos e métricas de qualidade para cada etapa." },
    ],
  },
];

type DimKey = "D" | "I" | "S" | "C";

export function getQuestionProgress(blockNumber: number, totalBlocks: number) {
  const current = Math.max(1, Math.min(blockNumber, totalBlocks));

  return {
    current,
    label: `${current} de ${totalBlocks}`,
    percent: Math.round(((current - 1) / totalBlocks) * 100),
  };
}

interface TiebreakOption {
  dim: string;
  text: string;
}

interface McResponseResult {
  blockCompleted: boolean;
  currentBlock: number;
  sessionCompleted: boolean;
  needsTiebreak: boolean;
  tiePair?: DimKey[];
  tiebreakScenario?: string;
  tiebreakOptions?: TiebreakOption[];
}

async function submitMcResponse(
  baseUrl: string,
  assessmentToken: string,
  blockNumber: number,
  mostLike: DimKey,
  leastLike: DimKey,
): Promise<McResponseResult> {
  const res = await fetch(`${baseUrl}api/chat/${assessmentToken}/mc-response`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ blockNumber, mostLike, leastLike }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `Erro ${res.status}`);
  }
  return res.json() as Promise<McResponseResult>;
}

function StatementRow({
  statement,
  mostSelected,
  leastSelected,
  onSelectMost,
  onSelectLeast,
}: {
  statement: string;
  mostSelected: boolean;
  leastSelected: boolean;
  onSelectMost: () => void;
  onSelectLeast: () => void;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border px-3 py-3 transition-all duration-150",
        mostSelected
          ? "border-primary/40 bg-primary/8"
          : leastSelected
          ? "border-destructive/40 bg-destructive/8"
          : "border-border bg-card",
      )}
    >
      <p className="flex-1 text-sm leading-relaxed text-foreground">{statement}</p>
      <div className="flex shrink-0 gap-4 pl-2">
        <button
          type="button"
          onClick={onSelectMost}
          aria-label="Mais"
          className={cn(
            "w-6 h-6 rounded-full border-2 transition-all duration-150 flex items-center justify-center",
            mostSelected
              ? "border-primary bg-primary"
              : "border-primary/40 bg-transparent hover:border-primary hover:bg-primary/10",
          )}
        >
          {mostSelected && <span className="w-2.5 h-2.5 rounded-full bg-white" />}
        </button>
        <button
          type="button"
          onClick={onSelectLeast}
          aria-label="Menos"
          className={cn(
            "w-6 h-6 rounded-full border-2 transition-all duration-150 flex items-center justify-center",
            leastSelected
              ? "border-destructive bg-destructive"
              : "border-destructive/40 bg-transparent hover:border-destructive hover:bg-destructive/10",
          )}
        >
          {leastSelected && <span className="w-2.5 h-2.5 rounded-full bg-white" />}
        </button>
      </div>
    </div>
  );
}

const MC_STORAGE_KEY = "disc_assessment_token";

export default function MultipleChoicePage() {
  const { assessmentToken } = useParams<{ assessmentToken: string }>();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const token = assessmentToken ?? (() => {
    try { return localStorage.getItem(MC_STORAGE_KEY) ?? ""; } catch { return ""; }
  })();

  const { data: session, isLoading, error } = useGetChatSession(token, {
    query: { enabled: !!token, queryKey: getGetChatSessionQueryKey(token), retry: false },
  });

  useEffect(() => {
    if (session?.status === "completed" || session?.status === "expired") {
      localStorage.removeItem(MC_STORAGE_KEY);
    }
  }, [session?.status]);

  useEffect(() => {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (error && status === 410) {
      localStorage.removeItem(MC_STORAGE_KEY);
    }
  }, [error]);

  const [currentBlockIndex, setCurrentBlockIndex] = useState<number | null>(null);
  const [mostLike, setMostLike] = useState<DimKey | null>(null);
  const [leastLike, setLeastLike] = useState<DimKey | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [tiebreakState, setTiebreakState] = useState<{
    scenario: string;
    options: TiebreakOption[];
  } | null>(null);
  const [tiebreakSelection, setTiebreakSelection] = useState<DimKey | null>(null);

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (!session) return;

    if (session.format === "discursive") {
      if (!redirectedRef.current) {
        redirectedRef.current = true;
        navigateRef.current(`/chat/${token}`, { replace: true });
      }
      return;
    }

    if (currentBlockIndex !== null) return;

    if (session.status === "completed") {
      setCurrentBlockIndex(-1);
      return;
    }

    const progress = session.mcProgress;
    if (progress) {
      if (progress.needsTiebreak && progress.tiebreakScenario && progress.tiebreakOptions?.length) {
        setCurrentBlockIndex(11);
        setTiebreakState({
          scenario: progress.tiebreakScenario,
          options: progress.tiebreakOptions as TiebreakOption[],
        });
      } else {
        const nextIdx = Math.max(0, Math.min(progress.submittedBlocks, MC_BLOCKS.length - 1));
        setCurrentBlockIndex(nextIdx);
      }
    } else {
      setCurrentBlockIndex(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, token, currentBlockIndex]);

  const currentBlock = currentBlockIndex !== null && currentBlockIndex >= 0
    ? MC_BLOCKS[currentBlockIndex]
    : undefined;

  const handleSelectMost = useCallback((dim: DimKey) => {
    setMostLike(dim);
    if (leastLike === dim) setLeastLike(null);
  }, [leastLike]);

  const handleSelectLeast = useCallback((dim: DimKey) => {
    setLeastLike(dim);
    if (mostLike === dim) setMostLike(null);
  }, [mostLike]);

  const handleSubmitBlock = useCallback(async () => {
    if (!mostLike || !leastLike || !currentBlock || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await submitMcResponse(
        import.meta.env.BASE_URL,
        token,
        currentBlock.blockNumber,
        mostLike,
        leastLike,
      );
      await queryClient.invalidateQueries({ queryKey: getGetChatSessionQueryKey(token) });
      if (result.sessionCompleted) {
        setCurrentBlockIndex(-1);
        return;
      }
      if (result.needsTiebreak && result.tiebreakScenario && result.tiebreakOptions) {
        setTiebreakState({ scenario: result.tiebreakScenario, options: result.tiebreakOptions });
        return;
      }
      setMostLike(null);
      setLeastLike(null);
      setCurrentBlockIndex((i) => Math.min((i ?? 0) + 1, MC_BLOCKS.length - 1));
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Erro ao enviar resposta");
    } finally {
      setSubmitting(false);
    }
  }, [mostLike, leastLike, currentBlock, submitting, token, queryClient]);

  const handleSubmitTiebreak = async () => {
    if (!tiebreakSelection || !tiebreakState) return;
    const loser = tiebreakState.options.find((o) => o.dim !== tiebreakSelection)?.dim as DimKey | undefined;
    if (!loser) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await submitMcResponse(
        import.meta.env.BASE_URL,
        token,
        13,
        tiebreakSelection,
        loser,
      );
      await queryClient.invalidateQueries({ queryKey: getGetChatSessionQueryKey(token) });
      if (result.sessionCompleted) {
        setCurrentBlockIndex(-1);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Erro ao enviar resposta");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">Link de avaliação inválido.</p>
      </div>
    );
  }

  if (isLoading || currentBlockIndex === null) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex gap-1">
          <span className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:0ms]" />
          <span className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:150ms]" />
          <span className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:300ms]" />
        </div>
      </div>
    );
  }

  if (error || !session) {
    const status = (error as { status?: number })?.status;
    return (
      <div className="flex flex-col items-center justify-center h-screen px-6 text-center space-y-3">
        <AlertCircle className="h-10 w-10 text-destructive" />
        <p className="font-medium text-lg">
          {status === 410 ? "Essa avaliação expirou." : "Sessão não encontrada."}
        </p>
        <p className="text-muted-foreground text-sm">
          {status === 410
            ? "O prazo para responder essa avaliação já passou."
            : "Verifique o link e tente novamente."}
        </p>
      </div>
    );
  }

  if (session.status === "expired") {
    return (
      <div className="flex items-center justify-center min-h-[100dvh] bg-background p-6">
        <div className="max-w-sm w-full text-center">
          <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-5">
            <Clock className="h-7 w-7 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Sessão Expirada</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            O tempo limite para esta avaliação foi atingido. Por favor, contate o administrador.
          </p>
        </div>
      </div>
    );
  }

  if (session.status === "completed" || currentBlockIndex === -1) {
    return (
      <div className="flex items-center justify-center min-h-[100dvh] bg-background p-6">
        <div className="max-w-sm w-full text-center animate-in fade-in zoom-in duration-500">
          <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
            <CheckCircle2 className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-xl font-semibold mb-3 text-foreground">Muito obrigado pela sua participação!</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            Suas respostas foram registradas com sucesso. Foi um prazer ter você aqui.
          </p>
          <div className="bg-muted/60 rounded-xl px-5 py-4 text-left">
            <p className="text-xs font-semibold text-foreground mb-1 uppercase tracking-wide">Próximos passos</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Seus resultados serão analisados pela equipe responsável. Em breve você receberá um retorno sobre as próximas etapas.
            </p>
          </div>
          <ParticipantRatingWidget
            assessmentToken={token}
            alreadyRated={session.participantRating != null}
          />
        </div>
      </div>
    );
  }

  const needsInfo = !session.participantName || !session.participantEmail;
  if (needsInfo) {
    return (
      <ParticipantInfoGate
        assessmentToken={token}
        onComplete={() => queryClient.invalidateQueries({ queryKey: getGetChatSessionQueryKey(token) })}
        existingName={session.participantName ?? undefined}
        existingEmail={session.participantEmail ?? undefined}
      />
    );
  }

  const totalBlocks = MC_BLOCKS.length;
  const questionProgress = getQuestionProgress(
    currentBlock?.blockNumber ?? currentBlockIndex + 1,
    totalBlocks,
  );
  const progress = tiebreakState ? 100 : questionProgress.percent;

  return (
    <div
      className="flex flex-col bg-background"
      style={{ minHeight: "100dvh" }}
    >
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b px-4 py-3">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-foreground">Avaliação DISC</span>
            {!tiebreakState && (
              <span className="text-xs text-muted-foreground">
                {questionProgress.label}
              </span>
            )}
          </div>
          <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-lg mx-auto space-y-6">
          {tiebreakState ? (
            <TiebreakScreen
              scenario={tiebreakState.scenario}
              options={tiebreakState.options}
              selection={tiebreakSelection}
              onSelect={setTiebreakSelection}
              onSubmit={handleSubmitTiebreak}
              submitting={submitting}
              error={submitError}
            />
          ) : currentBlock ? (
            <BlockScreen
              block={currentBlock}
              mostLike={mostLike}
              leastLike={leastLike}
              onSelectMost={handleSelectMost}
              onSelectLeast={handleSelectLeast}
              onSubmit={handleSubmitBlock}
              submitting={submitting}
              error={submitError}
            />
          ) : null}
        </div>
      </main>
    </div>
  );
}

function BlockScreen({
  block,
  mostLike,
  leastLike,
  onSelectMost,
  onSelectLeast,
  onSubmit,
  submitting,
  error,
}: {
  block: (typeof MC_BLOCKS)[0];
  mostLike: DimKey | null;
  leastLike: DimKey | null;
  onSelectMost: (d: DimKey) => void;
  onSelectLeast: (d: DimKey) => void;
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
}) {
  const bothSelected = !!mostLike && !!leastLike;

  return (
    <div className="space-y-5">
      <div className="rounded-xl bg-muted/60 border border-border px-4 py-3">
        <p className="text-xs font-semibold text-muted-foreground tracking-wide mb-0.5">{block.theme.toUpperCase()}</p>
        <p className="text-sm font-medium text-foreground leading-snug">
          Marque qual afirmativa descreve <span className="text-primary font-semibold">mais</span> e qual descreve <span className="text-destructive font-semibold">menos</span> você.
        </p>
      </div>

      <div>
        <div className="flex items-center gap-3 px-3 pb-2">
          <span className="flex-1" />
          <div className="flex shrink-0 gap-4 pl-2">
            <span className="w-6 text-center text-xs font-semibold text-primary">Mais</span>
            <span className="w-6 text-center text-xs font-semibold text-destructive">Menos</span>
          </div>
        </div>

        <div className="space-y-2">
          {block.statements.map((s) => (
            <StatementRow
              key={s.dim}
              statement={s.text}
              mostSelected={mostLike === s.dim}
              leastSelected={leastLike === s.dim}
              onSelectMost={() => onSelectMost(s.dim)}
              onSelectLeast={() => onSelectLeast(s.dim)}
            />
          ))}
        </div>
      </div>

      {error && (
        <p className="text-sm text-destructive text-center">{error}</p>
      )}

      <div>
        {submitting ? (
          <div className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce [animation-delay:0ms]" />
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce [animation-delay:150ms]" />
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce [animation-delay:300ms]" />
          </div>
        ) : (
          <Button
            className="w-full"
            size="lg"
            onClick={onSubmit}
            disabled={!bothSelected || submitting}
          >
            Avançar
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}

function TiebreakScreen({
  scenario,
  options,
  selection,
  onSelect,
  onSubmit,
  submitting,
  error,
}: {
  scenario: string;
  options: TiebreakOption[];
  selection: DimKey | null;
  onSelect: (d: DimKey) => void;
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
}) {
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Questão final</p>
        <p className="text-sm text-muted-foreground leading-relaxed">{scenario}</p>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold text-primary uppercase tracking-wide">Qual postura é mais natural para você?</p>
        <div className="space-y-3">
          {options.map((opt) => (
            <button
              key={opt.dim}
              type="button"
              onClick={() => onSelect(opt.dim as DimKey)}
              className={cn(
                "w-full text-left rounded-xl border-2 p-4 transition-all duration-150 text-sm leading-relaxed",
                selection === opt.dim
                  ? "border-primary bg-primary/10 text-primary font-medium"
                  : "border-border bg-card hover:border-primary/50 hover:bg-muted/50",
              )}
            >
              {opt.text}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p className="text-sm text-destructive text-center">{error}</p>
      )}

      <Button
        className="w-full"
        size="lg"
        onClick={onSubmit}
        disabled={!selection || submitting}
      >
        {submitting ? "Finalizando..." : "Concluir avaliação"}
        <CheckCircle2 className="h-4 w-4 ml-1" />
      </Button>
    </div>
  );
}
